/**
 * Intent-based script loader utilities
 * Provides smart loading strategies based on user intent and device type
 */

/**
 * Loads a script asynchronously with duplicate prevention
 * @param {string} scriptUrl - URL of the script to load
 * @param {string|null} checkGlobal - Optional window property name to check if script is already loaded (e.g., 'FitMix')
 * @returns {Promise} Resolves when script is loaded, rejects on error
 */
export function loadScriptAsync(scriptUrl, checkGlobal = null) {
  return new Promise((resolve, reject) => {
    if (checkGlobal && window[checkGlobal]) {
      resolve();
      return;
    }

    const existingScript = document.querySelector(`script[src="${scriptUrl}"]`);
    if (existingScript) {
      existingScript.addEventListener('load', resolve);
      existingScript.addEventListener('error', reject);
      return;
    }

    const script = document.createElement('script');
    script.src = scriptUrl;
    script.type = 'text/javascript';
    script.async = true;

    script.addEventListener('load', resolve);
    script.addEventListener('error', () => reject(new Error(`Failed to load script: ${scriptUrl}`)));

    document.head.appendChild(script);
  });
}

/**
 * Detects user intent and loads script accordingly
 * @param {string} scriptUrl - URL of the script to load
 * @param {HTMLElement} element - DOM element to observe for intent signals
 * @param {Object} options - Loading strategy options
 * @param {string} options.mobile - Mobile strategy: 'viewport' | 'immediate' (default: 'viewport')
 * @param {string} options.desktop - Desktop strategy: 'hover' | 'immediate' | 'idle' (default: 'hover')
 * @param {number} options.idleTimeout - Fallback timeout in ms for idle strategy (default: 5000)
 * @param {number} options.mobileBreakpoint - Breakpoint for mobile detection in px (default: 1000)
 * @param {string|null} options.checkGlobal - Window property to check if script already loaded
 * @param {string|null} options.sessionKey - SessionStorage key to check for prior usage
 * @param {string} options.sessionValue - SessionStorage value indicating prior usage (default: 'true')
 * @param {boolean} options.enableClickIntent - Allow clicks to trigger immediate script load (default: false)
 * @returns {Promise} Resolves when script is loaded
 */
export function loadScriptOnIntent(scriptUrl, element, options = {}) {
  const {
    mobile = 'viewport',
    desktop = 'hover',
    idleTimeout = 5000,
    mobileBreakpoint = 1000,
    checkGlobal = null,
    sessionKey = null,
    sessionValue = 'true',
    enableClickIntent = false
  } = options;

  return new Promise((resolve, reject) => {
    const isMobile = window.innerWidth < mobileBreakpoint;
    let strategy = isMobile ? mobile : desktop;

    if (sessionKey && sessionStorage.getItem(sessionKey) === sessionValue) {
      strategy = 'immediate';
    }

    let loaded = false;

    const loadScript = async () => {
      if (loaded) return;
      loaded = true;

      try {
        await loadScriptAsync(scriptUrl, checkGlobal);
        resolve();
      } catch (error) {
        reject(error);
      }
    };

    switch (strategy) {
      case 'immediate':
        loadScript();
        break;

      case 'viewport':
        if ('IntersectionObserver' in window) {
          const observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                observer.disconnect();
                loadScript();
              }
            });
          }, {
            rootMargin: '50px'
          });

          observer.observe(element);
        } else {
          loadScript();
        }
        break;

      case 'hover':
        const hoverHandler = () => {
          element.removeEventListener('mouseenter', hoverHandler);
          loadScript();
        };
        element.addEventListener('mouseenter', hoverHandler);

        Promise.all([
          new Promise(resolve => setTimeout(resolve, idleTimeout)),
          new Promise(resolve => {
            if ('requestIdleCallback' in window) {
              requestIdleCallback(resolve, { timeout: idleTimeout });
            } else {
              resolve();
            }
          })
        ]).then(loadScript);
        break;

      case 'idle':
        if ('requestIdleCallback' in window) {
          requestIdleCallback(() => loadScript(), { timeout: idleTimeout });
        } else {
          setTimeout(loadScript, idleTimeout);
        }
        break;

      default:
        reject(new Error(`Unknown loading strategy: ${strategy}`));
    }

    if (enableClickIntent) {
      const clickHandler = () => {
        element.removeEventListener('click', clickHandler);
        loadScript();
      };
      element.addEventListener('click', clickHandler);
    }
  });
}
